import type {DashboardFilterState} from '../hooks/useDashboardFilterPanel';
import type {DashboardDateField} from '../hooks/useDashboardFilters';
import type {TicketDashboardAnalyticsInput} from '../routes/__generated__/DashboardRouteQuery.graphql';

/**
 * The values `TicketDashboardAnalyticsInput.dateField` accepts on the server.
 * `updated_at` is what the backend calls "last activity"; the client's own
 * vocabulary uses `last_activity_at`, so the two are bridged here.
 */
export type DashboardAnalyticsDateField = 'created_at' | 'resolved_at' | 'updated_at';

/**
 * Client date-field token → server dateField value. `last_activity_at` is
 * mapped to `updated_at` deliberately: it is the nearest supported column, and
 * an unknown value would silently fall back to `created_at` at the backend
 * boundary rather than reaching the trend it was asked for.
 */
const DATE_FIELD_MAP: Record<DashboardDateField, DashboardAnalyticsDateField> = {
	created_at: 'created_at',
	resolved_at: 'resolved_at',
	last_activity_at: 'updated_at',
};

export function toAnalyticsDateField(field: DashboardDateField): DashboardAnalyticsDateField {
	return DATE_FIELD_MAP[field];
}

/**
 * Builds the analytics query input from the same filter state the table and the
 * totals row consume, so the gauge and the trend narrow to a set the reader can
 * recognise from the rows on screen.
 *
 * `dateField` drives the trend x-axis only — the gauge is deliberately always
 * resolved-in-period and ignores it on the server. `groupBy`, pagination and
 * sort are not analytics concerns and have no place here.
 *
 * The input is assembled by setting only keys that carry a value, so an unused
 * filter never ships as an explicit null (Relay serialises `undefined`
 * variables that way, and a null in place of "no filter" reads as "match
 * nothing" downstream).
 *
 * Some panel filters have no expression in the analytics input and are
 * excluded rather than silently dropped at the wire:
 *
 * - `slaState` — no `slaStatus` on `TicketDashboardAnalyticsInput`. The gauge
 *   already answers the SLA question over the resolved-in-period cohort; a
 *   further SLA-state narrowing has no obvious meaning against a trend of
 *   created/resolved counts.
 * - `lastActivityRange`, `resolvedDateRange`, `firstResponseRange` — the input
 *   carries only the top-level `from`/`to`. The trend re-buckets that one
 *   window against whichever `dateField` is selected; a second, independent
 *   date range against a different field is not something the shape can
 *   express, and layering it on top of `from`/`to` would silently narrow one
 *   axis by another.
 *
 * Extending the input to carry these would be a schema change (AGENTS.md §7);
 * until then the two charts respect the toolbar range and every list-shaped
 * filter, and the table and the totals row remain the surface where the panel
 * date ranges and SLA state live.
 *
 * `includeUnassigned` is the one input key with no filter behind it: the panel
 * has no unassigned toggle to drive it, so it is omitted here exactly as
 * `buildDashboardRollupsInput` and the ticket query omit it. Sending `false`
 * would not be neutral — it reads as "exclude unassigned".
 */
export function buildDashboardAnalyticsInput(
	applied: DashboardFilterState,
	from: string,
	to: string,
	dateField: DashboardDateField
): TicketDashboardAnalyticsInput {
	const input: Record<string, unknown> = {
		from,
		to,
		dateField: toAnalyticsDateField(dateField),
	};

	if (applied.status.length > 0) input.status = applied.status;
	if (applied.priority.length > 0) input.priority = applied.priority;
	if (applied.channel.length > 0) input.channel = applied.channel;
	if (applied.assignee.length > 0) input.assigneeId = applied.assignee;
	if (applied.requester.length > 0) input.requesterId = applied.requester;
	if (applied.tags.length > 0) input.labelId = applied.tags;

	// The panel holds its selections as plain strings (they are option ids from
	// the server), so the input's typed members are narrowed once, here, rather
	// than value-by-value: an unrecognised value is the server's to reject. This
	// mirrors `buildDashboardRollupsInput`.
	return input as TicketDashboardAnalyticsInput;
}
